/**
 * forming.ts - Parametry formowania termicznego
 * PlexiSystem Engineering Module
 */

import { MaterialKey } from './materials';

export interface FormingParameters {
  bend_C: [number, number];         // Temperatura gięcia liniowego [min, max]
  thermo_C: [number, number];       // Temperatura termoformowania [min, max]
  cool_active_min: [number, number]; // Czas chłodzenia aktywnego [min] dla 2-5mm
  cool_passive_min: [number, number]; // Czas chłodzenia pasywnego [min]
  min_radius_factor: number;         // Minimalny promień gięcia jako wielokrotność grubości
  notes: string[];                   // Uwagi specyficzne dla materiału
}

export const FORMING: Record<MaterialKey, FormingParameters> = {
  PMMA: {
    bend_C: [150, 170],
    thermo_C: [160, 190],
    cool_active_min: [15, 25],
    cool_passive_min: [30, 60],
    min_radius_factor: 3,
    notes: [
      "Relaksacja naprężeń 80-90°C przez 2-4h przy grubych elementach",
      "Nagrzewanie równomierne - unikać lokalnego przegrzania",
      "Możliwe pęcherzyki przy zbyt szybkim nagrzewaniu"
    ]
  },
  PETG: {
    bend_C: [105, 140],
    thermo_C: [140, 180],
    cool_active_min: [10, 20],
    cool_passive_min: [20, 40],
    min_radius_factor: 2,
    notes: [
      "Nie chłodzić wodą - możliwe pęknięcia",
      "Uwaga na pęcherze przy zbyt wysokiej temperaturze",
      "Doskonała formowalność - możliwe głębokie tłoczenie"
    ]
  },
  PC: {
    bend_C: [130, 150],
    thermo_C: [150, 190],
    cool_active_min: [20, 30],
    cool_passive_min: [30, 60],
    min_radius_factor: 3,
    notes: [
      "Minimalny promień gięcia ~3×grubość",
      "Odprężanie zalecane przy elementach precyzyjnych",
      "Wysoka temperatura formowania - uwaga na degradację"
    ]
  },
  HIPS: {
    bend_C: [90, 110],
    thermo_C: [120, 150],
    cool_active_min: [8, 15],
    cool_passive_min: [15, 30],
    min_radius_factor: 2,
    notes: [
      "Łatwe formowanie - niskie temperatury",
      "Możliwe lokalne wybielenia przy ostrym gięciu",
      "Dobra stabilność wymiarowa po schłodzeniu"
    ]
  },
  PVC: {
    bend_C: [80, 100],
    thermo_C: [100, 140],
    cool_active_min: [10, 20],
    cool_passive_min: [20, 35],
    min_radius_factor: 2.5,
    notes: [
      "Uwaga na degradację termiczną - nie przekraczać 140°C",
      "Możliwe żółknięcie przy przegrzaniu",
      "Sztywny PVC wymaga wyższych temperatur niż miękki"
    ]
  },
  ABS: {
    bend_C: [100, 120],
    thermo_C: [130, 160],
    cool_active_min: [12, 20],
    cool_passive_min: [20, 40],
    min_radius_factor: 2.5,
    notes: [
      "Dobra formowalność w szerokim zakresie temperatur",
      "Możliwe naprężenia wewnętrzne - zalecane wyżarzanie",
      "Zachowuje dobrą sztywność po formowaniu"
    ]
  }
};

/**
 * Oblicza czas nagrzewania materiału
 */
export function calculateHeatingTime(
  material: MaterialKey,
  thickness_mm: number,
  process: 'bend' | 'thermo'
): number {
  // Przybliżony czas nagrzewania: 1 minuta na mm grubości dla PMMA
  // Inne materiały mają współczynniki korekcyjne
  const baseTime = thickness_mm; // minuty
  
  const heatingFactors: Record<MaterialKey, number> = {
    PMMA: 1.0,
    PETG: 0.8,
    PC: 1.2,
    HIPS: 0.7,
    PVC: 0.9,
    ABS: 0.85
  };
  
  const processFactor = process === 'thermo' ? 1.2 : 1.0;
  
  return Math.ceil(baseTime * heatingFactors[material] * processFactor);
}

/**
 * Oblicza czas całkowity procesu formowania
 */
export function calculateTotalProcessTime(
  material: MaterialKey,
  thickness_mm: number,
  process: 'bend' | 'thermo',
  activeColing: boolean = true
): { heating: number; cooling: number; total: number } {
  const forming = FORMING[material];
  const heating = calculateHeatingTime(material, thickness_mm, process);
  
  // Interpolacja czasu chłodzenia dla różnych grubości
  const thicknessRange = [2, 5]; // zakres dla którego mamy dane
  const coolingRange = activeColing ? forming.cool_active_min : forming.cool_passive_min;
  
  // Prosta interpolacja liniowa
  const factor = Math.min(Math.max((thickness_mm - thicknessRange[0]) / (thicknessRange[1] - thicknessRange[0]), 0), 1);
  const cooling = Math.ceil(coolingRange[0] + (coolingRange[1] - coolingRange[0]) * factor);
  
  return {
    heating,
    cooling,
    total: heating + cooling
  };
}

/**
 * Oblicza minimalny promień gięcia
 */
export function calculateMinBendRadius(
  material: MaterialKey,
  thickness_mm: number
): number {
  const forming = FORMING[material];
  return thickness_mm * forming.min_radius_factor;
}

/**
 * Oblicza rozszerzalność termiczną
 */
export function calculateThermalExpansion(
  material: MaterialKey,
  length_mm: number,
  deltaT_C: number
): number {
  // Współczynniki rozszerzalności termicznej [1e-5/K]
  const thermalExpansionCoefficients: Record<MaterialKey, number> = {
    PMMA: 70,   // 70×10^-6 /K
    PETG: 70,   // 70×10^-6 /K
    PC: 65,     // 65×10^-6 /K
    HIPS: 80,   // 80×10^-6 /K
    PVC: 80,    // 80×10^-6 /K
    ABS: 90     // 90×10^-6 /K
  };
  
  const alpha = thermalExpansionCoefficients[material] * 1e-6; // Konwersja na /K
  
  return length_mm * alpha * deltaT_C;
}
